import { DateTime } from "luxon";

type BirthDate = DateTime | Date | string | null | undefined;

/** Checks the portions of an Italian fiscal code derivable from a person's name and birth date. */
export class FiscalCodeValidator {
    private static readonly monthCodes = "ABCDEHLMPRST";
    private static readonly omocodiaLetters = "LMNPQRSTUV";
    private static readonly omocodiaPositions = [6, 7, 9, 10, 12, 13, 14];
    private static readonly oddValues = [
        1, 0, 5, 7, 9, 13, 15, 17, 19, 21,
        1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23
    ];

    /** Returns true only for a real birth date that is not in the future. */
    static isValidBirthDate(value: BirthDate): boolean {
        const date = this.asDateTime(value);
        return date !== null && date.isValid && date.startOf("day") <= DateTime.local().startOf("day");
    }

    /** Checks format, control character, omocodia, and the name and birth-date sections. */
    static matches(fiscalCode: string, name: string, surname: string, birthDate: BirthDate): boolean {
        const date = this.asDateTime(birthDate);
        if (!fiscalCode || !name || !surname || !date || !this.isValidBirthDate(date)) return false;

        const code = fiscalCode.trim().toUpperCase();
        if (code.length !== 16 || !/^[A-Z0-9]{15}[A-Z]$/.test(code)) return false;
        if (this.controlCharacter(code.slice(0, 15)) !== code[15]) return false;

        const decoded = code.split("");
        for (const position of this.omocodiaPositions) {
            const letter = decoded[position];
            const digit = this.omocodiaLetters.indexOf(letter);
            if (digit >= 0) decoded[position] = String(digit);
        }

        const base = decoded.join("");
        if (!/^[A-Z]{6}[0-9]{2}[ABCDEHLMPRST][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(base)) return false;
        const encodedDay = Number(base.slice(9, 11));
        if (!((encodedDay >= 1 && encodedDay <= 31) || (encodedDay >= 41 && encodedDay <= 71))) return false;

        return base.slice(0, 3) === this.nameSection(surname, false)
            && base.slice(3, 6) === this.nameSection(name, true)
            && base.slice(6, 8) === String(date.year % 100).padStart(2, "0")
            && base[8] === this.monthCodes[date.month - 1]
            && encodedDay % 40 === date.day;
    }

    /** Converts picker, saved ISO, or JavaScript dates without changing their calendar day. */
    private static asDateTime(value: BirthDate): DateTime | null {
        if (DateTime.isDateTime(value)) return value;
        if (value instanceof Date) return DateTime.fromJSDate(value);
        if (typeof value === "string") return DateTime.fromISO(value, { setZone: true });
        return null;
    }

    /** Builds the three letters encoded for a surname or given name. */
    private static nameSection(value: string, givenName: boolean): string {
        const letters = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z]/g, "");
        const consonants = letters.replace(/[AEIOU]/g, "");
        const vowels = letters.replace(/[^AEIOU]/g, "");
        const selected = givenName && consonants.length >= 4
            ? consonants[0] + consonants[2] + consonants[3]
            : consonants.slice(0, 3);
        return (selected + vowels + "XXX").slice(0, 3);
    }

    /** Calculates the sixteenth character from the original fifteen characters. */
    private static controlCharacter(base: string): string {
        let sum = 0;
        for (let index = 0; index < base.length; index++) {
            const char = base[index];
            const value = /[0-9]/.test(char) ? Number(char) : char.charCodeAt(0) - 55;
            sum += index % 2 === 0 ? this.oddValues[value] : (value >= 10 ? value - 10 : value);
        }
        return String.fromCharCode(65 + sum % 26);
    }
}
